import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { EmptyState } from '@/components/States'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { relativeTime } from '@/lib/relative-time'
import { cn } from '@/lib/cn'
import type { AppNotification, NotificationKind } from '@/data/types'
import styles from './NotificationsPanel.module.css'

const FILTERS: ReadonlyArray<{ readonly id: NotificationKind | 'all'; readonly label: string }> = [
  { id: 'all', label: 'Everything' },
  { id: 'money-in', label: 'Money in' },
  { id: 'money-out', label: 'Money out' },
  { id: 'card', label: 'Card' },
  { id: 'security', label: 'Security' },
]

export function NotificationsScreen() {
  const repository = useRepository()
  const [items, setItems] = useState<readonly AppNotification[]>([])
  const [filter, setFilter] = useState<NotificationKind | 'all'>('all')

  const load = useCallback(() => {
    repository.listNotifications().then(setItems).catch(() => undefined)
  }, [repository])

  useEffect(() => load(), [load])

  const shown = items.filter((item) => (filter === 'all' ? true : item.kind === filter))
  const unread = items.filter((item) => !item.read).length

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle={unread > 0 ? `${unread} unread` : 'You are all caught up'}
        actions={
          unread > 0 ? (
            <Button
              variant="ghost"
              size="md"
              iconStart="done_all"
              onClick={async () => {
                await repository.markNotificationsRead()
                load()
              }}
            >
              Mark all read
            </Button>
          ) : undefined
        }
      />

      <Card tone="light" padded={false}>
        <Stack direction="row" gap={2} wrap style={{ padding: 'var(--pb-space-5)' }}>
          {FILTERS.map((item) => (
            <Button
              key={item.id}
              variant={filter === item.id ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </Button>
          ))}
        </Stack>

        {shown.length === 0 ? (
          <EmptyState icon="notifications_off" title="Nothing here" body="No notifications of this kind yet." />
        ) : (
          <div>
            {shown.map((item) => {
              const content = (
                <>
                  <span className={styles.glyph}>
                    <Icon name={item.icon} size={18} />
                  </span>
                  <Stack grow gap={1}>
                    <Stack direction="row" align="center" gap={2} wrap>
                      <Text variant="caption" weight="semibold">
                        {item.title}
                      </Text>
                      {!item.read && <Pill tone="accent">New</Pill>}
                    </Stack>
                    <Text variant="caption" tone="secondary">
                      {item.body}
                    </Text>
                    <Text variant="eyebrow" tone="tertiary">
                      {relativeTime(item.at)}
                    </Text>
                  </Stack>
                  {item.to && <Icon name="chevron_right" size={18} />}
                </>
              )
              const className = cn(styles.item, !item.read && styles.unreadItem)
              return item.to ? (
                <Link key={item.id} to={item.to} className={className}>
                  {content}
                </Link>
              ) : (
                <div key={item.id} className={className}>
                  {content}
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </>
  )
}
